













































































































import { defineComponent, computed, reactive, ref } from '@nuxtjs/composition-api';
import type { PropType } from '@nuxtjs/composition-api';
import LazyHydrate from 'vue-lazy-hydration';
import { isEmpty } from 'lodash';
import { useIntersectionObserver } from '@vueuse/core';
import type { Category, ProductVariant } from '~/types/commercetools';
import { ProductVariantExtended } from '~/types/product/ProductVariantExtended';
import type { BrandsType } from '~/constants/brands';
import type { ProductDescription } from '~/types/product/ProductDescription';
import {
  PRODUCT_DESCRIPTION,
  TECHNICAL_DETAILS,
  ABOUT_BRAND,
} from '~/constants/pdpDetailSections';
import { useAccessories, useProductExtended } from '~/composables';
import { PDP_NAV_ANCHORS } from '~/constants/pdpNavAnchors';
import type { ProductCardSize } from '~/constants/productCardSize';
import { PRODUCT_CARD_SIZE } from '~/constants/productCardSize';
import AnchorNavigationSectionWrapper from '~/components/molecules/Product/AnchorNavigationSectionWrapper.vue';
import RelatedProductsSkeleton from '~/components/molecules/PdpSkeletons/RelatedProductsSkeleton.vue';
import ProductAnchorTitle from '~/components/atoms/Product/ProductAnchorTitle/ProductAnchorTitle.vue';
import type { ComparisonProductData } from '~/types/product/ComparisonProducts';
import type { ProductProcessedAttributes } from '~/types/product/attribute/ProductProcessedAttributes';
import DeliveryPackage from '~/components/molecules/Product/details/DeliveryPackage.vue';

export default defineComponent({
  components: {
    ProductDescription: () => import(/* webpackChunkName: "ProductDescription" */
      '~/components/molecules/Product/ProductDescription.vue'),
    TechnicalDetails: () => import(/* webpackChunkName: "TechnicalDetails" */
      '~/components/molecules/Product/details/TechnicalDetails.vue'),
    ComparisonProducts: () => import(/* webpackChunkName: "ComparisonProducts" */
      '~/components/organisms/ComparisonProducts/ComparisonProducts.vue'),
    AboutTheBrand: () => import(/* webpackChunkName: "AboutTheBrand" */
      '~/components/molecules/Product/AboutTheBrand.vue'),
    RelatedProducts: () => import(/* webpackChunkName: "RelatedProducts" */
      '~/components/organisms/Product/RelatedProducts/RelatedProducts.vue'),
    BloomreachSimilarProducts: () => import(/* webpackChunkName: "BloomreachSimilarProducts" */
      '~/components/organisms/Product/Bloomreach/BloomreachSimilarProducts/BloomreachSimilarProducts.vue'),
    PdpSiblingCategories: () => import(/* webpackChunkName: "PdpSiblingCategories" */
      '~/components/molecules/Product/PdpSiblingCategories/PdpSiblingCategories.vue'),
    LazyHydrate,
    AnchorNavigationSectionWrapper,
    RelatedProductsSkeleton,
    ProductAnchorTitle,
    DeliveryPackage,
  },
  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      required: true,
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      default: () => {},
    },
    productDescription: {
      type: Object as PropType<ProductDescription>,
      default: () => {
        return {};
      },
    },
    aboutBrand: {
      type: Object as PropType<BrandsType>,
      default: () => {
        return {};
      },
    },
    relatedAccessories: {
      type: Array as PropType<ProductVariant[]>,
      required: true,
    },
    relatedAccessoriesLoading: {
      type: Boolean,
      required: true,
    },
    parentsForAccessory: {
      type: Array as PropType<ProductVariant[]>,
      required: true,
    },
    parentsForAccessoryLoading: {
      type: Boolean,
      required: true,
    },
    shouldShowRelatedAccessories: {
      type: Boolean,
      required: true,
    },
    shouldShowParentsForAccessory: {
      type: Boolean,
      required: true,
    },
    productPrice: {
      type: Number,
      required: true,
    },
    size: {
      type: String as PropType<ProductCardSize>,
      default: PRODUCT_CARD_SIZE.RESPONSIVE,
      validator: (value: ProductCardSize) => {
        return Object.values(PRODUCT_CARD_SIZE).includes(value);
      },
    },
    siblingCategories: {
      type: Array as PropType<Category[]>,
      required: true,
    },
    comparisonProductsData: {
      type: Array as PropType<ComparisonProductData[]>,
      default: () => [],
    },
  },
  setup(props, { root: { $route } }) {
    const isProductDescriptionEmpty = computed(() => isEmpty(props.productDescription));
    const isAboutBrandEmpty = computed(() => isEmpty(props.aboutBrand));
    const { slug } = $route.params;
    const { loaded: productDataLoaded } = useProductExtended(slug);

    const productSku = computed(() => props.product?.sku || '');

    const relatedAccessoriesNode = ref<HTMLElement | null>(null);
    const relatedParentProductsNode = ref<HTMLElement | null>(null);

    const allAccordionCurrentState = reactive({
      [PRODUCT_DESCRIPTION]: true,
      [TECHNICAL_DETAILS]: true,
      [ABOUT_BRAND]: true,
    });

    const deliveryPackage = computed(() => props.attributesLocalized?.deliveryPackage || []);

    const onAccordionClicked = (accordionTag) => {
      allAccordionCurrentState[accordionTag] = !allAccordionCurrentState[accordionTag];
    };

    const {
      onIntersectAccessoriesOnce,
      onIntersectParentProductsOnce,
      onClickAccessory,
      onClickParentProduct,
    } = useAccessories({
      suggestedAccessories: props.relatedAccessories,
      suggestedParentProducts: props.parentsForAccessory,
      sourceProductSku: productSku.value,
    });

    useIntersectionObserver(relatedAccessoriesNode, onIntersectAccessoriesOnce);
    useIntersectionObserver(relatedParentProductsNode, onIntersectParentProductsOnce);

    const accessoryClicked = (accessorySku: string) => {
      onClickAccessory(accessorySku);
    };

    const parentClicked = (parentSku: string) => {
      onClickParentProduct(parentSku);
    };

    return {
      isProductDescriptionEmpty,
      PRODUCT_DESCRIPTION,
      PRODUCT_CARD_SIZE,
      TECHNICAL_DETAILS,
      ABOUT_BRAND,
      isAboutBrandEmpty,
      onAccordionClicked,
      allAccordionCurrentState,
      productDataLoaded,
      PDP_NAV_ANCHORS,
      slug,
      relatedAccessoriesNode,
      relatedParentProductsNode,
      productSku,
      deliveryPackage,
      accessoryClicked,
      parentClicked,
    };
  },
});
